import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './accounting.css';

const AddVoucher = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    type: 'receipt',
    voucher_date: new Date().toISOString().split('T')[0],
    account_id: '',
    amount: '',
    description: '',
    notes: '',
    payment_method: 'cash',
    check_number: '',
    bank_name: '',
    reference_number: '',
    currency: 'SAR',
    exchange_rate: '1'
  });

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      const response = await axios.get('/api/vouchers/accounts');
      if (response.data.status) {
        setAccounts(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching accounts:', error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await axios.post('/api/vouchers', formData);
      if (response.data.status) {
        alert('تم إضافة السند بنجاح');
        navigate('/vouchers');
      }
    } catch (error) {
      console.error('Error creating voucher:', error);
      alert('حدث خطأ أثناء إضافة السند');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/vouchers');
  };

  return (
    <div className="vouchers-container">
      <div className="page-header">
        <div className="page-title">
          <h3>{t('accounting.vouchers.addVoucher')}</h3>
          <p>{t('accounting.vouchers.help.description')}</p>
        </div>
        <button 
          type="button" 
          className="back-btn"
          onClick={handleCancel}
        >
          ← {t('accounting.vouchers.actions.back')}
        </button>
      </div>

      <div className="form-container">
        <div className="help-panels">
          <div className="help-panel">
            <div className="panel-header">
              <span className="info-icon">ℹ️</span>
              <h6>{t('accounting.vouchers.help.title')}</h6>
            </div>
            <div className="panel-content">
              <div className="help-item">
                <strong>{t('accounting.vouchers.voucherTypes.receipt')}</strong>
                <p>{t('accounting.vouchers.help.receiptInfo')}</p>
              </div>
              <div className="help-item">
                <strong>{t('accounting.vouchers.voucherTypes.payment')}</strong>
                <p>{t('accounting.vouchers.help.paymentInfo')}</p>
              </div>
              <div className="help-item">
                <strong>{t('accounting.vouchers.status')}</strong>
                <p>{t('accounting.vouchers.help.statusInfo')}</p>
              </div>
              <div className="help-item">
                <strong>{t('accounting.vouchers.paymentMethod')}</strong>
                <p>{t('accounting.vouchers.help.paymentMethodsInfo')}</p>
              </div>
            </div>
          </div>

          <div className="guide-panel">
            <div className="panel-header">
              <span className="info-icon">📋</span>
              <h6>{t('accounting.vouchers.guide.title')}</h6>
            </div>
            <div className="panel-content">
              <div className="account-type-guide">
                <div className="guide-item receipt">
                  <div className="guide-title receipt-color">{t('accounting.vouchers.guide.receipt.title')}</div>
                  <p>{t('accounting.vouchers.guide.receipt.description')}</p>
                </div>
                <div className="guide-item payment">
                  <div className="guide-title payment-color">{t('accounting.vouchers.guide.payment.title')}</div>
                  <p>{t('accounting.vouchers.guide.payment.description')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="form-section">
          <form onSubmit={handleSubmit} className="account-form">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label required">{t('accounting.vouchers.type')}</label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleInputChange}
                  required
                  className="form-control"
                >
                  <option value="receipt">{t('accounting.vouchers.voucherTypes.receipt')}</option>
                  <option value="payment">{t('accounting.vouchers.voucherTypes.payment')}</option>
                </select>
                <div className="form-note">اختر نوع السند: قبض أو صرف</div>
              </div>

              <div className="form-group">
                <label className="form-label required">{t('accounting.vouchers.voucherDate')}</label>
                <input
                  type="date"
                  name="voucher_date"
                  value={formData.voucher_date}
                  onChange={handleInputChange}
                  required
                  className="form-control"
                />
                <div className="form-note">تاريخ السند</div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label required">{t('accounting.vouchers.account')}</label>
                <select
                  name="account_id"
                  value={formData.account_id}
                  onChange={handleInputChange}
                  required
                  className="form-control"
                >
                  <option value="">اختر الحساب</option>
                  {accounts.map(account => (
                    <option key={account.id} value={account.id}>
                      {account.code} - {account.name}
                    </option>
                  ))}
                </select>
                <div className="form-note">اختر الحساب المرتبط بالسند</div>
              </div>

              <div className="form-group">
                <label className="form-label required">{t('accounting.vouchers.amount')}</label>
                <input
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleInputChange}
                  required
                  min="0.01"
                  step="0.01"
                  className="form-control"
                  placeholder="0.00"
                />
                <div className="form-note">المبلغ بالريال السعودي</div>
              </div>
            </div>

            <div className="form-group">
              <label className="form-label required">{t('accounting.vouchers.description')}</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                required
                className="form-control"
                rows="3"
                placeholder="وصف السند..."
              />
              <div className="form-note">وصف تفصيلي للسند</div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label required">{t('accounting.vouchers.paymentMethod')}</label>
                <select
                  name="payment_method"
                  value={formData.payment_method}
                  onChange={handleInputChange}
                  required
                  className="form-control"
                >
                  <option value="cash">{t('accounting.vouchers.paymentMethods.cash')}</option>
                  <option value="bank_transfer">{t('accounting.vouchers.paymentMethods.bank_transfer')}</option>
                  <option value="check">{t('accounting.vouchers.paymentMethods.check')}</option>
                  <option value="card">{t('accounting.vouchers.paymentMethods.card')}</option>
                </select>
                <div className="form-note">طريقة الدفع المستخدمة</div>
              </div>

              <div className="form-group">
                <label className="form-label">{t('accounting.vouchers.referenceNumber')}</label>
                <input
                  type="text"
                  name="reference_number"
                  value={formData.reference_number}
                  onChange={handleInputChange}
                  className="form-control"
                  placeholder="رقم المرجع..."
                />
                <div className="form-note">رقم المرجع (اختياري)</div>
              </div>
            </div>

            {formData.payment_method === 'check' && (
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">{t('accounting.vouchers.checkNumber')}</label>
                  <input
                    type="text"
                    name="check_number"
                    value={formData.check_number}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="رقم الشيك..."
                  />
                  <div className="form-note">رقم الشيك (اختياري)</div>
                </div>

                <div className="form-group">
                  <label className="form-label">{t('accounting.vouchers.bankName')}</label>
                  <input
                    type="text"
                    name="bank_name"
                    value={formData.bank_name}
                    onChange={handleInputChange}
                    className="form-control"
                    placeholder="اسم البنك..."
                  />
                  <div className="form-note">اسم البنك (اختياري)</div>
                </div>
              </div>
            )}

            <div className="form-group">
              <label className="form-label">{t('accounting.vouchers.notes')}</label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                className="form-control"
                rows="3"
                placeholder="ملاحظات إضافية..."
              />
              <div className="form-note">ملاحظات إضافية (اختياري)</div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn-cancel"
                onClick={handleCancel}
                disabled={loading}
              >
                {t('accounting.vouchers.actions.cancel')}
              </button>
              <button
                type="submit"
                className="btn-save"
                disabled={loading}
              >
                {loading ? 'جاري الحفظ...' : t('accounting.vouchers.actions.save')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddVoucher;
